// Login único pela janelinha, no modelo "Entrar com o Google": a janelinha faz o login na
// Área do Cliente, avisa a janela principal e se fecha; quem entra no painel é a janela principal.
//
// Por que não acompanhar a janelinha por `popup.location` nem por `window.opener`/`window.name`:
// ao passar pela Área do Cliente (outro site) o navegador pode cortar o vínculo entre as duas
// janelas e o Chrome limpa `window.name` na navegação entre sites. Nas duas versões anteriores
// isso fez o login terminar DENTRO da janelinha, com as duas janelas logadas.
// O aviso aqui vai por BroadcastChannel (com `storage` de reserva), que só depende de as duas
// janelas serem da mesma origem — e são, porque o callback do login devolve a janelinha para cá.

const CHANNEL = "dadospj-sso";
const PENDING_KEY = "dadospj-sso-pendente";
const PENDING_TTL_MS = 10 * 60 * 1000;
const DONE_KEY = "dadospj-sso-concluido";
const ERROR_KEY = "dadospj-sso-erro";

function markPending() {
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch {
    // Sem storage, a janelinha não se reconhece e o login termina nela: degrada, não quebra.
  }
}

function takePending() {
  try {
    const at = Number(localStorage.getItem(PENDING_KEY));
    localStorage.removeItem(PENDING_KEY);
    return Number.isFinite(at) && Date.now() - at < PENDING_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Janela principal: abre a janelinha e espera o aviso para ir ao painel.
 * `onError(codigo)` é chamado quando a janelinha caiu em `/login?erro=<codigo>` (SSO falhou) —
 * quem decide o que mostrar é quem chamou (LoginPage), esta função só repassa o código.
 */
export function startSsoLogin(entryUrl, features, onError) {
  markPending();
  const popup = window.open(entryUrl, "dadospj-sso", features);
  if (!popup) {
    // Pop-up bloqueado: página inteira. O destino do callback já é o painel (ou o erro, na volta).
    window.location.assign(entryUrl);
    return;
  }

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    try {
      popup.close();
    } catch {
      // A própria janelinha também se fecha; esta é só a segunda tentativa.
    }
    window.location.assign("/dashboard");
  };
  const fail = (codigo) => {
    if (finished) return;
    finished = true;
    try {
      popup.close();
    } catch {
      // idem finish(): a janelinha também tenta se fechar sozinha.
    }
    if (onError) onError(codigo);
  };

  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (event) => {
      const data = event.data;
      if (data === "concluido") finish();
      else if (data && data.tipo === "erro") fail(data.codigo);
    };
  }
  window.addEventListener("storage", (event) => {
    if (event.key === DONE_KEY && event.newValue) finish();
    if (event.key === ERROR_KEY && event.newValue) fail(event.newValue);
  });
}

/**
 * Chamada no carregamento do app. Cobre dois casos de "esta janela é a janelinha voltando":
 * login concluído em qualquer tela, ou SSO que falhou e caiu em `/login?erro=<codigo>`. Nos
 * dois, avisa a janela principal pelo canal existente e tenta se fechar. Devolve `true` quando
 * o fechamento foi pedido; o app não precisa ser montado (evita abrir pop-up de dentro de pop-up).
 */
export function finishSsoPopupIfLanded() {
  const path = window.location.pathname;

  if (path === "/login") {
    const erro = new URLSearchParams(window.location.search).get("erro");
    if (!erro) return false;
    if (!takePending()) return false;

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(CHANNEL);
      channel.postMessage({ tipo: "erro", codigo: erro });
      channel.close();
    }
    try {
      localStorage.setItem(ERROR_KEY, erro);
      localStorage.removeItem(ERROR_KEY);
    } catch {
      // BroadcastChannel já avisou.
    }

    window.close();
    return window.closed;
  }

  if (path.startsWith("/api1/")) return false;
  if (!takePending()) return false;

  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage("concluido");
    channel.close();
  }
  try {
    localStorage.setItem(DONE_KEY, String(Date.now()));
    localStorage.removeItem(DONE_KEY);
  } catch {
    // BroadcastChannel já avisou.
  }

  // Só fecha janela aberta por script. Se o login foi de página inteira (pop-up bloqueado), o
  // close é ignorado e esta própria janela segue para o painel (ou mostra o erro, se houver).
  window.close();
  return window.closed;
}
